<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('button.add_new_m', { var: $t('global.item') })"
    size="large"
  >
    <template #body>
      <div class="flex min-h-0 w-full flex-col gap-3">
        <div class="relative">
          <FontAwesomeIcon
            :icon="['fal', 'search']"
            class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-gray-400"
          />
          <input
            v-model="search"
            type="search"
            :placeholder="$t('global.search')"
            class="form-control w-full pl-9"
          />
        </div>

        <ul v-if="errorMessages.length" class="text-sm text-red-600">
          <li v-for="message in errorMessages" :key="message">
            {{ message }}
          </li>
        </ul>

        <ul class="flex flex-col gap-1.5">
          <li
            v-for="item in filteredItems"
            :key="item.id"
            class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2 transition-colors dark:border-slate-600"
            :class="
              selection[item.id]
                ? 'border-primary-300 bg-primary-50 dark:bg-slate-600'
                : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-600'
            "
            @click="selection[item.id] ? null : toggle(item)"
          >
            <input
              type="checkbox"
              class="form-check-input shrink-0"
              :checked="!!selection[item.id]"
              @click.stop="toggle(item)"
            />
            <span class="min-w-0 flex-1 truncate">{{ item.name }}</span>

            <div
              v-if="selection[item.id]"
              class="flex items-center gap-2 text-sm"
            >
              <label class="flex items-center gap-1">
                <span class="text-gray-500">
                  {{ $t('attributes.quantity') }}
                </span>
                <input
                  v-model.number="selection[item.id].quantity"
                  type="number"
                  min="1"
                  class="form-control w-16 px-2 py-1 text-right"
                />
              </label>
              <label class="flex items-center gap-1">
                <input
                  v-model.number="selection[item.id].price_excluding_vat"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control w-24 px-2 py-1 text-right"
                />
                <span class="text-gray-500">€</span>
              </label>
              <label class="flex items-center gap-1">
                <span class="text-gray-500">-</span>
                <input
                  v-model.number="selection[item.id].reduction"
                  type="number"
                  min="0"
                  max="100"
                  class="form-control w-16 px-2 py-1 text-right"
                />
                <span class="text-gray-500">%</span>
              </label>
            </div>
            <span v-else class="shrink-0 text-sm text-gray-500">
              {{ $h.formatCurrency(item.price_excluding_vat) }}
            </span>
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <BaseButton
        :title="addTitle"
        icon="plus"
        color="primary"
        :disabled="!selectedCount"
        @click="submitForm"
      />
      <BaseButton :title="$t('button.close')" color="dark" @click="close" />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapActions, mapGetters } from 'vuex'

import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'

/**
 * Adds catalogue items to an invoice: tick the lines, adjust quantity, price
 * and reduction on the ticked ones. The catalogue itself is left untouched.
 */
export default {
  name: 'invoiceItemAddDialog',
  components: { BaseCenteredModal, FontAwesomeIcon },
  props: {
    isModalVisible: { type: Boolean, required: true }
  },
  emits: ['close', 'backdropClicked'],
  data() {
    return { search: '', selection: {} }
  },
  computed: {
    ...mapGetters({
      itemList: 'item/getItemList',
      errors: 'auth/getErrors'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalVisible
      },
      set(value) {
        if (!value) this.close()
      }
    },
    filteredItems() {
      const term = this.search.trim().toLowerCase()
      const items = this.itemList ?? []
      return term
        ? items.filter((item) => item.name?.toLowerCase().includes(term))
        : items
    },
    selectedCount() {
      return Object.keys(this.selection).length
    },
    addTitle() {
      const title = this.$t('button.add')
      return this.selectedCount ? `${title} (${this.selectedCount})` : title
    },
    // Laravel keys them `items.N.field`.
    errorMessages() {
      return Object.values(this.errors ?? {})
        .flat()
        .map((message) => (this.$te(message) ? this.$t(message) : message))
    }
  },
  watch: {
    isModalVisible(visible) {
      if (visible) {
        this.search = ''
        this.selection = {}
      }
    }
  },
  mounted() {
    this.fetchAllItem()
  },
  methods: {
    ...mapActions({
      fetchAllItem: 'item/fetchAllItem',
      putMultipleInvoiceItems: 'invoicing/updateMultipleInvoiceItems'
    }),
    toggle(item) {
      if (this.selection[item.id]) {
        delete this.selection[item.id]
        return
      }
      this.selection[item.id] = {
        item_id: item.id,
        name: item.name,
        price_excluding_vat: Number(item.price_excluding_vat) || 0,
        quantity: 1,
        reduction: 0
      }
    },
    close() {
      this.$emit('close')
    },
    submitForm() {
      this.putMultipleInvoiceItems({
        invoiceId: this.$route.params.invoicing_id,
        items: { items: Object.values(this.selection) }
      }).then(this.close)
    }
  }
}
</script>
